import React from 'react';
import { css } from '@emotion/core';
import { theme } from '@weave/theme-original';
import { NakedButton, LocationIcon, XIcon } from '@weave/design-system';

import { LocationSearchContainer } from '../../location';
import { LocationHistoryModel } from '../../../models/location-history.model';
import { OrgHistoryModel } from '../../../models/org-history.model';
import { formatDateFromUnixTimestamp } from '../../../helpers/utils';
import { Loader } from '../../shared/loader/loader.component';
import { LOCATION, ORG_DETAILS, injectParams } from '../../../constants/paths';

type HistoryEntryType = 'location' | 'org';

interface HistoryEntry {
  type: HistoryEntryType;
  id: string;
  name: string;
  slug?: string;
  created: number;
}

interface Props {
  canCreateLocations: boolean;
  history: any;
  locationHistoryList: LocationHistoryModel[];
  orgHistoryList: OrgHistoryModel[];
  loading: boolean;

  loadLocationHistory: () => {};
  loadOrgHistory: () => {};
  removeLocationFromHistory: (location: LocationHistoryModel) => void;
  removeOrgFromHistory: (org: OrgHistoryModel) => void;
  setLocation: (locationId: string) => void;
}

export class HomeComponent extends React.Component<Props> {
  constructor(props) {
    super(props);
    this.props.loadLocationHistory();
    this.props.loadOrgHistory();
  }

  addLocation = (event: React.MouseEvent) => {
    event.preventDefault();
    this.props.history.push(LOCATION.create);
  };

  getHistoryEntries = (): HistoryEntry[] => {
    const locationEntries: HistoryEntry[] = this.props.locationHistoryList.map(
      (location) => ({
        type: 'location',
        id: location.id,
        name: location.name,
        slug: location.slug,
        created: location.created,
      })
    );
    const orgEntries: HistoryEntry[] = this.props.orgHistoryList.map((org) => ({
      type: 'org',
      id: org.id,
      name: org.name,
      created: org.created,
    }));

    return [...locationEntries, ...orgEntries].sort((a, b) => b.created - a.created);
  };

  selectHistoryEntry = (entry: HistoryEntry) => {
    if (entry.type === 'org') {
      this.props.history.push(injectParams(ORG_DETAILS, { orgId: entry.id }));
    } else {
      this.props.setLocation(entry.id);
    }
  };

  deleteHistory = (entry: HistoryEntry, event: React.MouseEvent) => {
    event.stopPropagation();
    if (entry.type === 'org') {
      this.props.removeOrgFromHistory(
        this.props.orgHistoryList.find((org) => org.id === entry.id)!
      );
    } else {
      this.props.removeLocationFromHistory(
        this.props.locationHistoryList.find((location) => location.id === entry.id)!
      );
    }
  };

  render() {
    const historyEntries = this.getHistoryEntries();

    return (
      <div className="container-fluid home">
        <div
          css={css`
            margin: 40px auto;
            width: min(650px, calc(100vw - ${theme.spacing(4)}));
            text-align: center;

            .location-search-row {
              display: flex;
              align-items: center;
              justify-content: center;
            }

            .location-search-box {
              display: inline-block;
              width: 90%;
            }

            .large-icon {
              position: relative;
              top: -3px;
              left: 5px;

              svg {
                max-height: 25px;
                max-width: 25px;
              }
            }
          `}
        >
          <h1>Search</h1>
          <div className="location-search-row">
            <LocationSearchContainer className="location-search-box" />
            {this.props.canCreateLocations && (
              <NakedButton
                css={css`
                  svg:hover {
                    transform: scale(1.2);
                  }
                `}
                onClick={this.addLocation}
              >
                <LocationIcon color="success" className="large-icon" />
              </NakedButton>
            )}
          </div>
        </div>
        <hr />
        {this.props.loading ? (
          <Loader />
        ) : (
          <div
            className="history-section"
            css={css`
              text-align: center;
            `}
          >
            <h1
              css={css`
                margin-bottom: ${theme.spacing(2)};
              `}
            >
              History
            </h1>
            <span>Click on a row to open that location or organization</span>
            <table
              className="table table-striped table-bordered table-hover"
              css={css`
                max-width: 800px;
                margin: auto;
                text-align: left;
              `}
            >
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Name</th>
                  <th>ID / Slug</th>
                  <th>Last Used</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {historyEntries.map((entry) => {
                  return (
                    <tr
                      css={css`
                        cursor: pointer;
                      `}
                      key={`${entry.type}-${entry.id}`}
                      onClick={() => this.selectHistoryEntry(entry)}
                    >
                      <td>{entry.type === 'org' ? 'Organization' : 'Location'}</td>
                      <td>{entry.name}</td>
                      <td>
                        {entry.id}
                        {entry.slug && (
                          <div
                            css={css`
                              margin-top: ${theme.spacing(0.5)};
                            `}
                          >
                            {entry.slug}
                          </div>
                        )}
                      </td>
                      <td>{formatDateFromUnixTimestamp(entry.created)}</td>
                      <td>
                        <NakedButton
                          css={css`
                            svg:hover {
                              transform: scale(1.2);
                            }
                          `}
                          onClick={(event) => this.deleteHistory(entry, event)}
                        >
                          <XIcon color="error" />
                        </NakedButton>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }
}
